import { ReactNode } from 'react';
import { Spinner } from 'react-bootstrap';
import { FiAlertCircle, FiCheckCircle, FiInfo } from 'react-icons/fi';
import * as S from './styles';

export type HintTone = 'error' | 'info' | 'success' | 'warning';

export interface FieldHint {
  tone: HintTone;
  text: ReactNode;
  loading?: boolean;
}

interface FieldShellProps {
  label: string;
  required?: boolean;
  error?: string;
  hint?: FieldHint | null;
  children: ReactNode;
}

/** Rótulo em cima, campo, e logo abaixo o erro (ou uma dica). */
export function FieldShell({ label, required, error, hint, children }: FieldShellProps) {
  const shown: FieldHint | null | undefined = error ? { tone: 'error', text: error } : hint;

  return (
    <S.Field $invalid={Boolean(error)}>
      <span className="field-label">
        {label}
        {required && <em aria-hidden="true">*</em>}
      </span>
      {children}
      {shown && (
        <S.Hint $tone={shown.tone} role={shown.tone === 'error' ? 'alert' : undefined}>
          {shown.loading ? (
            <Spinner animation="border" size="sm" style={{ width: 10, height: 10 }} />
          ) : shown.tone === 'error' || shown.tone === 'warning' ? (
            <FiAlertCircle size={11} />
          ) : shown.tone === 'success' ? (
            <FiCheckCircle size={11} />
          ) : (
            <FiInfo size={11} />
          )}
          <span>{shown.text}</span>
        </S.Hint>
      )}
    </S.Field>
  );
}

interface SegmentedProps<T extends string> {
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel: string;
}

export function SegmentedControl<T extends string>({
  value,
  options,
  onChange,
  disabled,
  ariaLabel,
}: SegmentedProps<T>) {
  return (
    <S.Segmented role="radiogroup" aria-label={ariaLabel}>
      {options.map((option) => (
        <S.SegmentButton
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          $active={value === option.value}
          disabled={disabled}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </S.SegmentButton>
      ))}
    </S.Segmented>
  );
}
